import { atom, useAtom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { useMemo } from 'react';
import type { RouterOutputs } from 'trpc/shared';

type Product = RouterOutputs['product']['getProductsInfinite']['products'][number];

export const productSelectionAtom = atom<Product[]>([]);

const productSelectedAtomFamily = atomFamily((product: Product) =>
    atom(
        get => get(productSelectionAtom).find(item => item.id === product.id) !== undefined,
        (get, set, nextValue: boolean) => {
            const productSelection = get(productSelectionAtom);
            const inProducts = get(productSelectionAtom).find(item => item.id === product.id) !== undefined;

            if (nextValue === false && inProducts) {
                set(
                    productSelectionAtom,
                    productSelection.filter(item => item.id !== product.id),
                );
            }
            if (nextValue === true && !inProducts) {
                set(productSelectionAtom, [...productSelection, product]);
            }
        },
    ),
);

export const useProductSelected = (product: Product) => {
    const productSelectedAtom = useMemo(() => productSelectedAtomFamily(product), [product]);

    const [selected, setSelected] = useAtom(productSelectedAtom);
    const selectionMode = useAtomValue(useMemo(() => atom(get => get(productSelectionAtom).length > 0), []));

    return useMemo(
        () => ({
            selectionMode,
            selected,
            setSelected,
        }),
        [selectionMode, selected, setSelected],
    );
};
